import React from 'react';
import {
  AlertCircle,
  FileAudio,
  FileText,
  Film,
  Image as ImageIcon,
  Loader2,
  RotateCcw,
  X,
} from 'lucide-react';
import { formatBytes } from '@/lib/productionAttachments';

function typeIcon(type) {
  if (type === 'video') return Film;
  if (type === 'image') return ImageIcon;
  if (type === 'audio' || type === 'recording') return FileAudio;
  return FileText;
}

/**
 * Compact attachment icons with remove (×). Shown above the prompt as soon as
 * a file is picked (including while uploading).
 */
export function AttachmentChips({ attachments, onRemove, onRetry }) {
  if (!attachments?.length) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid="attachment-chips">
      {attachments.map((att) => {
        const type = att.type || att.kind || 'document';
        const Icon = typeIcon(type);
        const status = att.processing_status || att.status || 'uploaded';
        const failed = status === 'failed';
        const busy = status === 'uploading' || status === 'uploaded' || status === 'processing';
        const thumb = att.thumbnailUrl || att.previewUrl;

        return (
          <div
            key={att.id || att.localId}
            className={`relative group inline-flex items-center justify-center w-12 h-12 rounded-xl border shrink-0 ${
              failed
                ? 'border-red-400/50 bg-red-500/15'
                : 'border-white/15 bg-white/[0.08]'
            }`}
            title={
              att.error ||
              `${att.filename || 'attachment'}${att.size ? ` · ${formatBytes(att.size)}` : ''} · ${status}`
            }
          >
            {thumb && (type === 'image' || type === 'video') ? (
              <img
                src={thumb}
                alt=""
                className="absolute inset-0 w-full h-full rounded-[11px] object-cover"
              />
            ) : (
              <Icon
                className={`w-5 h-5 ${failed ? 'text-red-300' : 'text-[#2DD4BF]'}`}
                strokeWidth={2}
              />
            )}

            {busy ? (
              <span className="absolute inset-0 rounded-[11px] bg-black/45 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-white" />
              </span>
            ) : null}

            {failed ? (
              <span className="absolute bottom-0.5 left-0.5">
                <AlertCircle className="w-3 h-3 text-red-300" />
              </span>
            ) : null}

            {failed && onRetry ? (
              <button
                type="button"
                onClick={() => onRetry(att)}
                className="absolute -bottom-1.5 -left-1.5 w-5 h-5 rounded-full bg-[#1a1f2a] border border-white/20 flex items-center justify-center text-[#9CA3AF] hover:text-white cursor-pointer z-10"
                aria-label={`Retry ${att.filename}`}
              >
                <RotateCcw className="w-2.5 h-2.5" />
              </button>
            ) : null}

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove?.(att);
              }}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#0f1218] border border-white/25 flex items-center justify-center text-white/80 hover:text-white hover:bg-red-500/80 hover:border-red-400 cursor-pointer z-10 shadow"
              aria-label={`Remove ${att.filename || 'attachment'}`}
              data-testid="attachment-remove"
            >
              <X className="w-3 h-3" strokeWidth={2.5} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default AttachmentChips;
